(() => {
  if (window.SlideshowScheduler?.initAll) {
    window.SlideshowScheduler.initAll(document);
    return;
  }

  const MAX_TIMEOUT = 2147483647;

  function showStaticFallback(component, slides, activeSlides) {
    clearInterval(component.autoplay);
    clearTimeout(component.setPositionTimeout);
    component.scheduleFallback = true;
    slides.forEach((slide) => {
      const isActive = activeSlides.includes(slide);
      const isCurrent = slide === activeSlides[0];
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
      if (isCurrent) slide.removeAttribute('tabindex');
      else slide.setAttribute('tabindex', '-1');
      slide.querySelectorAll('a').forEach((link) => {
        if (isCurrent) link.removeAttribute('tabindex');
        else link.setAttribute('tabindex', '-1');
      });
    });
    component.hidden = activeSlides.length === 0;
    component.querySelectorAll('.slideshow__controls').forEach((controls) => { controls.hidden = true; });
    component.sliderItemsToShow = activeSlides;
    component.sliderControlLinksArray = [];
    component.sliderFirstItemNode = activeSlides[0] || null;
    component.currentPage = activeSlides.length ? 1 : 0;
    component.totalPages = activeSlides.length;
    const slider = component.querySelector('.slideshow');
    if (slider && activeSlides.length) slider.scrollTo({ left: activeSlides[0].offsetLeft, behavior: 'instant' });
    component.removeAttribute('data-schedule-pending');
  }

  class SlideshowSchedule {
    constructor(component) {
      this.component = component;
      this.slider = component.querySelector('.slideshow');
      this.slides = Array.from(component.querySelectorAll('.slideshow__slide[data-block-id]'));
      this.controls = Array.from(component.querySelectorAll('.slider-counter__link[data-slide-block-id]'));
      this.controlsWrapper = component.querySelector('.slideshow__controls');
      this.autoplayEnabled = this.slider?.dataset.autoplay === 'true';
      this.selectedBlockId = null;
      this.timeout = null;
      this.animationFrame = null;
      this.imageAttributes = new WeakMap();
      this.invalidSchedules = new Set();
      this.apply = this.apply.bind(this);

      this.slides.forEach((slide) => {
        slide.querySelectorAll('img').forEach((image) => {
          this.imageAttributes.set(image, {
            fetchpriority: image.getAttribute('fetchpriority'),
            loading: image.getAttribute('loading'),
          });
        });
      });
    }

    destroy() {
      clearTimeout(this.timeout);
      cancelAnimationFrame(this.animationFrame);
    }

    selectBlock(blockId) {
      this.selectedBlockId = blockId;
      this.apply();
      this.component.pause?.();
    }

    deselectBlock(blockId) {
      if (this.selectedBlockId !== blockId) return;
      this.selectedBlockId = null;
      this.apply();
    }

    reportInvalidSchedule(slide) {
      const blockId = slide.dataset.blockId;
      slide.setAttribute('data-schedule-error', '');
      if (this.invalidSchedules.has(blockId)) return;

      this.invalidSchedules.add(blockId);
      console.warn(
        `[Slideshow scheduler] Block ${blockId} has an invalid schedule. ` +
          'Use YYYY-MM-DD for dates and HH:mm for times, and ensure End is after Start. ' +
          'The slide remains visible.'
      );
    }

    apply() {
      clearTimeout(this.timeout);
      cancelAnimationFrame(this.animationFrame);

      const now = Date.now();
      const boundaries = [];
      const invalidSlides = [];
      const activeSlides = this.slides.filter((slide) => {
        try {
          const schedule = scheduler.getSchedule(slide, now);
          if (!schedule.valid) invalidSlides.push(slide);
          if (schedule.nextBoundary) boundaries.push(schedule.nextBoundary);
          return schedule.active || slide.dataset.blockId === this.selectedBlockId;
        } catch (error) {
          console.error('[Slideshow scheduler] Could not evaluate a slide.', error);
          return slide.dataset.scheduleEnabled !== 'true';
        }
      });

      try {
        this.applyVisibility(activeSlides);
        this.slides.forEach((slide) => {
          if (invalidSlides.includes(slide)) this.reportInvalidSchedule(slide);
          else {
            slide.removeAttribute('data-schedule-error');
            this.invalidSchedules.delete(slide.dataset.blockId);
          }
        });
        this.updateComponent(activeSlides);
      } catch (error) {
        this.showFallback(activeSlides, error);
      } finally {
        this.component.removeAttribute('data-schedule-pending');
        this.scheduleNextUpdate(boundaries);
      }
    }

    applyVisibility(activeSlides) {
      this.slides.forEach((slide) => this.setSlideState(slide, activeSlides.includes(slide)));
      this.component.hidden = activeSlides.length === 0;
      if (this.controlsWrapper) this.controlsWrapper.hidden = activeSlides.length <= 1;
    }

    showFallback(activeSlides, error) {
      console.error('[Slideshow scheduler] Showing static fallback.', error);
      showStaticFallback(this.component, this.slides, activeSlides);
      this.updateCounter(this.component.currentPage, activeSlides.length);
    }

    setSlideState(slide, isActive) {
      slide.hidden = !isActive;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      if (!isActive) {
        slide.setAttribute('tabindex', '-1');
        slide.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
      }

      const control = this.controls.find((item) => item.dataset.slideBlockId === slide.dataset.blockId);
      if (!control) return;

      control.hidden = !isActive;
      control.disabled = !isActive;
      control.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (!isActive) control.removeAttribute('aria-current');
    }

    updateComponent(activeSlides) {
      const activeBlockIds = new Set(activeSlides.map((slide) => slide.dataset.blockId));
      const activeControls = this.controls.filter((control) => activeBlockIds.has(control.dataset.slideBlockId));
      const currentSlide = this.component.setAvailableSlides(activeSlides, activeControls);
      this.updateLabels(activeSlides, activeControls);
      this.updateImagePriority(activeSlides[0]);

      if (activeSlides.length <= 1) this.component.pause?.();

      if (!activeSlides.length || !this.slider) {
        this.updateCounter(0, 0);
        return;
      }

      const currentIndex = Math.max(activeSlides.indexOf(currentSlide), 0);
      this.component.currentPage = currentIndex + 1;
      this.component.totalPages = activeSlides.length;
      this.updateCounter(currentIndex + 1, activeSlides.length);

      this.animationFrame = requestAnimationFrame(() => {
        try {
          this.slider.scrollTo({ left: currentSlide.offsetLeft, behavior: 'instant' });
          this.component.sliderItems = this.component.querySelectorAll('[id^="Slide-"]');
          this.component.sliderItemsToShow = activeSlides;
          this.component.sliderFirstItemNode = activeSlides[0];

          if (activeSlides.length > 1) {
            this.component.initPages?.();
            this.component.currentPage = currentIndex + 1;
            this.updateCounter(currentIndex + 1, activeSlides.length);
          }

          this.component.setSlideVisibility?.();
          this.updateCurrentControl(activeControls, currentIndex);

          if (
            activeSlides.length > 1 &&
            this.autoplayEnabled &&
            this.component.autoplayButtonIsSetToPlay !== false
          ) {
            this.component.play?.();
          }
        } catch (error) {
          this.showFallback(activeSlides, error);
        }
      });
    }

    updateLabels(activeSlides, activeControls) {
      const total = activeSlides.length;
      const slideLabel = this.component.dataset.slideLabel || 'Load slide';
      const ofLabel = this.component.dataset.slideOfLabel || 'of';

      activeSlides.forEach((slide, index) => {
        slide.setAttribute('aria-label', `${index + 1} ${ofLabel} ${total}`);
      });

      activeControls.forEach((control, index) => {
        control.setAttribute('aria-label', `${slideLabel} ${index + 1} ${ofLabel} ${total}`);
        if (control.classList.contains('slider-counter__link--numbers')) {
          control.textContent = index + 1;
        }
      });
    }

    updateCounter(current, total) {
      if (this.component.currentPageElement) this.component.currentPageElement.textContent = current;
      if (this.component.pageTotalElement) this.component.pageTotalElement.textContent = total;
    }

    updateCurrentControl(controls, currentIndex) {
      controls.forEach((control, index) => {
        const isCurrent = index === currentIndex;
        control.classList.toggle('slider-counter__link--active', isCurrent);
        if (isCurrent) control.setAttribute('aria-current', 'true');
        else control.removeAttribute('aria-current');
      });
    }

    updateImagePriority(firstActiveSlide) {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('img').forEach((image) => {
          const original = this.imageAttributes.get(image);
          if (!original) return;
          this.restoreAttribute(image, 'fetchpriority', original.fetchpriority);
          this.restoreAttribute(image, 'loading', original.loading);
        });
      });

      firstActiveSlide?.querySelectorAll('img').forEach((image) => {
        image.setAttribute('fetchpriority', 'high');
        image.setAttribute('loading', 'eager');
      });
    }

    restoreAttribute(element, name, value) {
      if (value === null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    }

    scheduleNextUpdate(boundaries) {
      if (!boundaries.length) return;

      const delay = Math.max(Math.min(Math.min(...boundaries) - Date.now() + 50, MAX_TIMEOUT), 0);
      this.timeout = window.setTimeout(this.apply, delay);
    }
  }

  const instances = new Map();

  const scheduler = {
    getSchedule: window.SlideshowScheduler.getSchedule,
    init(component) {
      if (!(component instanceof HTMLElement)) return;
      if (instances.has(component)) {
        instances.get(component).apply();
        return;
      }

      if (!component.querySelector('.slideshow__slide[data-schedule-enabled="true"]')) {
        component.removeAttribute('data-schedule-pending');
        return;
      }

      let instance;
      try {
        instance = new SlideshowSchedule(component);
        instances.set(component, instance);
        instance.apply();
      } catch (error) {
        instance?.destroy();
        instances.delete(component);
        console.error('[Slideshow scheduler] Initialization failed.', error);
        const slides = Array.from(component.querySelectorAll('.slideshow__slide[data-block-id]'));
        const fallbackSlides = slides.filter((slide) => slide.dataset.scheduleEnabled !== 'true');
        showStaticFallback(component, slides, fallbackSlides);
      } finally {
        component.removeAttribute('data-schedule-pending');
      }
    },

    initAll(root) {
      if (root.matches?.('slideshow-component')) this.init(root);
      root.querySelectorAll?.('slideshow-component').forEach((component) => this.init(component));
    },

    destroy(component) {
      instances.get(component)?.destroy();
      instances.delete(component);
    },

    selectBlock(slide) {
      const component = slide.closest('slideshow-component');
      instances.get(component)?.selectBlock(slide.dataset.blockId);
    },

    deselectBlock(slide) {
      const component = slide.closest('slideshow-component');
      instances.get(component)?.deselectBlock(slide.dataset.blockId);
    },
  };

  window.SlideshowScheduler = scheduler;

  window.addEventListener('focus', () => scheduler.initAll(document));
  window.addEventListener('pageshow', () => scheduler.initAll(document));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduler.initAll(document);
  });

  document.addEventListener('slideshow:ready', (event) => scheduler.init(event.detail.component));
  document.addEventListener('shopify:section:load', (event) => scheduler.initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('slideshow-component').forEach((component) => scheduler.destroy(component));
  });
  document.addEventListener('shopify:block:select', (event) => {
    if (event.target.matches('.slideshow__slide[data-block-id]')) scheduler.selectBlock(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    if (event.target.matches('.slideshow__slide[data-block-id]')) scheduler.deselectBlock(event.target);
  });

  scheduler.initAll(document);
})();
